import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { money } from '../../utils/format';
import { ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Field, NumberInput, Select, TextInput } from '../../components/ui/Form';

interface Row {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  version: number;
  decimals?: number;
  nature?: string;
  is_system?: boolean;
  group_name?: string | null;
  category?: string | null;
  unit?: string | null;
}

type Kind = {
  key: string;
  label: string;
  /** One word for a single entry, e.g. "expense", used in buttons and prompts. */
  one: string;
  icon: string;
  blurb: string;
  /** Spending and earning heads are accounts, so they are made a different way. */
  nature?: 'EXPENSE' | 'INCOME';
  route: string;
  codeHint: string;
  decimals?: boolean;
  /** Stones carry a kind and the unit they are weighed or counted in. */
  stone?: boolean;
  example: string;
};

const KINDS: Kind[] = [
  {
    key: 'expense',
    label: 'Expense types',
    one: 'expense type',
    icon: '💸',
    blurb:
      'What the shop spends on — rent, salaries, tea, van diesel. Every expense you record is filed under one of these, and the profit report groups by them.',
    nature: 'EXPENSE',
    route: '/expense-categories',
    codeHint: 'Numbered for you automatically.',
    example: 'Vehicle fuel',
  },
  {
    key: 'income',
    label: 'Other income types',
    one: 'income type',
    icon: '🪙',
    blurb: 'Money coming in that is not a jewellery sale — locker rent, old-gold scrap, interest.',
    nature: 'INCOME',
    route: '/income-categories',
    codeHint: 'Numbered for you automatically.',
    example: 'Locker rent',
  },
  {
    key: 'categories',
    label: 'Product categories',
    one: 'category',
    icon: '💍',
    blurb: 'Chains, bangles, rings — how stock is grouped in reports.',
    route: '/categories',
    codeHint: 'Short code',
    example: 'Chains',
  },
  {
    key: 'collections',
    label: 'Collections',
    one: 'collection',
    icon: '✨',
    blurb: 'Bridal, festival, daily wear — a range a piece belongs to.',
    route: '/collections',
    codeHint: 'Short code',
    example: 'Bridal',
  },
  {
    key: 'designs',
    label: 'Designs',
    one: 'design',
    icon: '🎨',
    blurb: 'Named patterns your karigar works to.',
    route: '/designs',
    codeHint: 'Short code',
    example: 'Kolam',
  },
  {
    key: 'brands',
    label: 'Brands',
    one: 'brand',
    icon: '🏷️',
    blurb: 'For shops that carry branded lines alongside their own.',
    route: '/brands',
    codeHint: 'Short code',
    example: 'In-house',
  },
  {
    key: 'departments',
    label: 'Departments',
    one: 'department',
    icon: '🏬',
    blurb: 'Counters or sections, for reporting.',
    route: '/departments',
    codeHint: 'Short code',
    example: 'Gold counter',
  },
  {
    key: 'stones',
    label: 'Stone types',
    one: 'stone type',
    icon: '💎',
    blurb: 'Diamonds, rubies, pearls — what goes into a piece, and whether it is weighed in carats or counted.',
    route: '/stone-types',
    codeHint: 'Short code',
    example: 'Ruby',
    stone: true,
  },
  {
    key: 'uoms',
    label: 'Units',
    one: 'unit',
    icon: '⚖️',
    blurb: 'Gram, piece, pair — how quantities are counted.',
    route: '/uoms',
    codeHint: 'Short code',
    example: 'Gram',
    decimals: true,
  },
];

const STONE_CATEGORIES = [
  { value: 'DIAMOND', label: 'Diamond' },
  { value: 'PRECIOUS', label: 'Precious' },
  { value: 'SEMI_PRECIOUS', label: 'Semi-precious' },
  { value: 'SYNTHETIC', label: 'Synthetic / CZ' },
  { value: 'PEARL', label: 'Pearl' },
  { value: 'OTHER', label: 'Other' },
];
const STONE_UNITS = [
  { value: 'CARAT', label: 'Carats' },
  { value: 'GRAM', label: 'Grams' },
  { value: 'PIECE', label: 'Pieces' },
];

/**
 * Every list a shop fills in for itself.
 *
 * Spending and earning heads sit at the top because they are the ones a shop
 * actually adds as it goes — a new kind of expense turns up most months — and
 * because they are not really "masters" at all: each one is an account in the
 * chart, which is why an expense booked to it reaches the P&L and the trial
 * balance without anything special happening.
 *
 * Nothing here is ever deleted once it has been used. A head that is no longer
 * wanted is retired: what was already spent under it still reads correctly in
 * last year's accounts, and it simply stops being offered on new entries.
 */
export function MastersPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const [params, setParams] = useSearchParams();
  // Spending and earning heads are accounts: a role that cannot see expenses
  // or income is not shown those tabs, rather than tabs that fail to load.
  const kinds = KINDS.filter(
    (k) => (k.nature !== 'EXPENSE' || can('expense.view')) && (k.nature !== 'INCOME' || can('income.view')),
  );
  const kind = kinds.find((k) => k.key === params.get('list')) ?? kinds[0]!;
  const [editing, setEditing] = useState<Row | null>(null);
  const [retiring, setRetiring] = useState<Row | null>(null);
  const [form, setForm] = useState({ code: '', name: '', decimals: '3', category: 'PRECIOUS', unit: 'CARAT' });
  const [error, setError] = useState<unknown>(null);

  const mayEdit = can('master.manage');
  // Retiring is this app's delete, and deleting is the owner's call.
  const mayRetire = can('document.void');

  const list = useQuery({
    queryKey: ['master', kind.key],
    queryFn: () => api.get<{ data: Row[] }>(`${kind.route}${kind.nature ? '' : '?limit=200'}`),
  });
  // Spending heads are shown with what has actually been spent under them.
  const spend = useQuery({
    queryKey: ['expense-summary'],
    queryFn: () =>
      api.get<{ rows?: { category: string; amount: string }[]; data?: { category: string; amount: string }[] }>(
        '/reports/expense-summary/run',
      ),
    enabled: kind.key === 'expense' && can('report.view_financial'),
  });
  const spentBy = new Map<string, string>();
  for (const r of spend.data?.rows ?? spend.data?.data ?? []) {
    spentBy.set(r.category.split(' ')[0]!, r.amount);
  }

  const reset = (): void => {
    setForm({ code: '', name: '', decimals: '3', category: 'PRECIOUS', unit: 'CARAT' });
    setEditing(null);
  };

  const save = useMutation({
    mutationFn: () => {
      if (kind.nature) {
        return editing
          ? api.patch(`/account-categories/${editing.id}`, { name: form.name.trim() })
          : api.post('/account-categories', {
              nature: kind.nature,
              name: form.name.trim(),
              ...(form.code.trim() ? { code: form.code.trim() } : {}),
            });
      }
      const body = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        ...(kind.decimals ? { decimals: Number(form.decimals || 0) } : {}),
        ...(kind.stone ? { category: form.category, unit: form.unit } : {}),
      };
      return editing ? api.patch(`${kind.route}/${editing.id}`, body, editing.version) : api.post(kind.route, body);
    },
    onSuccess: () => {
      toast(editing ? `${form.name} updated` : `${form.name} added`);
      void client.invalidateQueries({ queryKey: ['master', kind.key] });
      void client.invalidateQueries({ queryKey: ['accounts'] });
      reset();
      setError(null);
    },
    onError: setError,
  });

  const retire = useMutation({
    mutationFn: (row: Row) =>
      kind.nature
        ? api.patch(`/account-categories/${row.id}`, { isActive: !row.is_active })
        : api.patch(`${kind.route}/${row.id}`, { is_active: !row.is_active }, row.version),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['master', kind.key] });
      setRetiring(null);
    },
    onError: (e) => {
      setError(e);
      setRetiring(null);
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (form.name.trim() && (kind.nature || form.code.trim())) save.mutate();
  };

  const rows = list.data?.data ?? [];
  const live = rows.filter((r) => r.is_active !== false);
  const retired = rows.filter((r) => r.is_active === false);

  return (
    <>
      <PageHeader
        title="Lists and categories"
        subtitle="The short lists this shop fills in for itself — used everywhere else in the app."
      />
      <ErrorNotice error={error ?? list.error} />

      {/* Pick a list by tapping its tile — friendlier than a column of words,
          and it shows at a glance what each one is for. */}
      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-5 lg:grid-cols-9">
        {kinds.map((k) => (
          <button
            key={k.key}
            onClick={() => {
              setParams({ list: k.key });
              reset();
            }}
            className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition ${
              kind.key === k.key
                ? 'border-gold-500 bg-gold-50 shadow-card'
                : 'border-silver-100 bg-surface hover:border-gold-300 hover:bg-gold-50/40'
            }`}
          >
            <span className="text-xl leading-none">{k.icon}</span>
            <span
              className={`text-2xs leading-tight ${kind.key === k.key ? 'font-semibold text-gold-700' : 'text-ink-soft'}`}
            >
              {k.label}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/* The chosen list */}
        <div>
          <div className="mb-3 flex items-start gap-2">
            <span className="text-2xl leading-none">{kind.icon}</span>
            <div>
              <h2 className="text-base font-semibold">{kind.label}</h2>
              <p className="text-sm text-ink-soft">{kind.blurb}</p>
            </div>
          </div>

          {list.isLoading ? <Loading rows={4} /> : null}

          {/* An empty list invites the first entry rather than just saying none. */}
          {!list.isLoading && rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-silver-400 bg-surface px-6 py-10 text-center">
              <p className="text-3xl">{kind.icon}</p>
              <p className="mt-2 font-medium">No {kind.label.toLowerCase()} yet</p>
              <p className="mt-1 text-sm text-ink-soft">
                {mayEdit ? `Add your first one on the right — try “${kind.example}”.` : 'Nothing has been added yet.'}
              </p>
            </div>
          ) : null}

          {/* Each entry as a card: name large, code and figures quiet beneath,
              actions on the right. Reads like a list a person keeps, not a grid. */}
          {live.length > 0 ? (
            <div className="space-y-2">
              {live.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between rounded-lg border border-silver-100 bg-surface px-3 py-2.5 shadow-card"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{r.name}</p>
                    <p className="num text-2xs text-ink-soft">
                      {r.code}
                      {kind.decimals ? ` · ${r.decimals ?? 0} decimals` : ''}
                      {kind.stone
                        ? ` · ${STONE_CATEGORIES.find((c) => c.value === r.category)?.label ?? ''} · ${STONE_UNITS.find((u) => u.value === r.unit)?.label ?? ''}`
                        : ''}
                      {r.is_system ? ' · built-in' : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {kind.key === 'expense' && spentBy.get(r.code) ? (
                      <span className="num text-sm font-medium text-ink-soft" title="Spent so far">
                        {money(spentBy.get(r.code))}
                      </span>
                    ) : null}
                    {mayEdit ? (
                      <>
                        <button
                          className="text-2xs text-gold-700 underline"
                          onClick={() => {
                            setEditing(r);
                            setForm({
                              code: r.code,
                              name: r.name,
                              decimals: String(r.decimals ?? 3),
                              category: r.category ?? 'PRECIOUS',
                              unit: r.unit ?? 'CARAT',
                            });
                          }}
                        >
                          Edit
                        </button>
                        {!r.is_system && mayRetire ? (
                          <button className="text-2xs text-ink-soft underline" onClick={() => setRetiring(r)}>
                            Retire
                          </button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* Retired entries, folded away — kept for history, out of the way. */}
          {retired.length > 0 ? (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-ink-soft">
                {retired.length} retired {retired.length === 1 ? 'entry' : 'entries'}
              </summary>
              <div className="mt-2 space-y-2">
                {retired.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between rounded-lg border border-silver-100 bg-silver-50/60 px-3 py-2 opacity-80"
                  >
                    <div>
                      <p className="font-medium">{r.name}</p>
                      <p className="num text-2xs text-ink-soft">{r.code} · retired</p>
                    </div>
                    {mayRetire ? (
                      <button className="text-2xs text-gold-700 underline" onClick={() => setRetiring(r)}>
                        Bring back
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </div>

        {/* Add / rename, right where the list is */}
        {mayEdit ? (
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="card h-fit space-y-3">
            <h2 className="text-sm font-semibold">
              {editing ? `Edit “${editing.name}”` : `Add ${/^[aeiou]/i.test(kind.one) ? 'an' : 'a'} ${kind.one}`}
            </h2>

            <Field label="Name" required>
              <TextInput
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                maxLength={120}
                placeholder={kind.example}
              />
            </Field>

            {!editing ? (
              <Field label="Code" required={!kind.nature} hint={kind.codeHint}>
                {kind.nature ? (
                  <TextInput
                    value={form.code}
                    onChange={(v) => setForm({ ...form, code: v.replace(/\D/g, '') })}
                    maxLength={8}
                    placeholder="auto"
                  />
                ) : (
                  <TextInput
                    value={form.code}
                    onChange={(v) => setForm({ ...form, code: v })}
                    uppercase
                    maxLength={32}
                    allow={/[A-Z0-9\-_]/}
                    placeholder="e.g. CHN"
                  />
                )}
              </Field>
            ) : (
              <p className="text-2xs text-ink-soft">
                The code stays <span className="num">{editing.code}</span>. Entries already recorded point at it.
              </p>
            )}

            {kind.stone ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Kind">
                  <Select
                    value={form.category}
                    onChange={(v) => setForm({ ...form, category: v || 'OTHER' })}
                    options={STONE_CATEGORIES}
                  />
                </Field>
                <Field label="Measured in">
                  <Select
                    value={form.unit}
                    onChange={(v) => setForm({ ...form, unit: v || 'CARAT' })}
                    options={STONE_UNITS}
                  />
                </Field>
              </div>
            ) : null}

            {kind.decimals && !editing ? (
              <Field label="Decimal places" hint="Grams need three; pieces need none.">
                <NumberInput
                  value={form.decimals}
                  onChange={(v) => setForm({ ...form, decimals: v })}
                  decimals={0}
                  max={4}
                />
              </Field>
            ) : null}

            <div className="flex gap-2">
              <button className="btn-primary flex-1" disabled={save.isPending || !form.name.trim()}>
                {save.isPending ? 'Saving…' : editing ? 'Save' : `Add ${kind.one}`}
              </button>
              {editing ? (
                <button type="button" className="btn-ghost" onClick={reset}>
                  Cancel
                </button>
              ) : null}
            </div>
          </form>
        ) : null}
      </div>

      <ConfirmDialog
        open={!!retiring}
        tone={retiring?.is_active === false ? 'primary' : 'danger'}
        title={retiring?.is_active === false ? 'Use this again?' : `Retire ${retiring?.name ?? ''}?`}
        message={
          retiring?.is_active === false
            ? 'It will be offered again on new entries.'
            : 'Everything already recorded under it stays exactly as it is — last year’s accounts still read correctly. It simply stops being offered on new entries.'
        }
        confirmLabel={retiring?.is_active === false ? 'Bring it back' : 'Retire it'}
        busy={retire.isPending}
        onCancel={() => setRetiring(null)}
        onConfirm={() => retiring && retire.mutate(retiring)}
      />
    </>
  );
}
